// contexts/SocketContext.js
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import { fetchOwnProfile, getStoredToken } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, "");
const ACK_TIMEOUT_MS = 10000;
const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

// One connection per page; the login token is sent in the handshake so the server only lets this socket
// join its own user room. Every chat event also carries the token, as the API expects.
export const SocketProvider = ({ children }) => {
  const token = getStoredToken();
  const [socket, setSocket] = useState(null);
  // Start from the id saved at login (with the token) so cached chats render on the right side and the
  // socket connects at once; the full profile replaces it when it arrives
  const [currentUser, setCurrentUser] = useState(() => {
    const storedId = Number(sessionStorage.getItem("userID") || localStorage.getItem("userID"));
    return token && storedId ? { id: storedId } : null;
  });
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchOwnProfile(token)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data?.result?.id) setCurrentUser(data.result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const userId = currentUser?.id;

  useEffect(() => {
    if (!token || !userId) return;
    const s = io(API_ORIGIN, { auth: { token } });

    // Runs again after every reconnect, so the rooms and online status are restored
    const onConnect = () => {
      setIsConnected(true);
      s.emit("join", userId);
      s.emit("user-online", JSON.stringify({ userId, isOnline: true }));
    };
    const onDisconnect = () => setIsConnected(false);

    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    setSocket(s);

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [token, userId]);

  // Emits a chat event with the token and resolves with the server's acknowledgement
  const emitWithAck = useCallback(
    (event, payload) =>
      new Promise((resolve, reject) => {
        if (!socket) {
          reject(new Error("Chat is not connected. Please try again."));
          return;
        }
        socket
          .timeout(ACK_TIMEOUT_MS)
          .emit(event, JSON.stringify({ ...payload, token }), (err, res) => {
            if (err) reject(new Error("The server did not respond. Please try again."));
            else if (!res?.success) reject(new Error(res?.error || "Something went wrong."));
            else resolve(res);
          });
      }),
    [socket, token]
  );

  // Fire-and-forget events (typing indicator)
  const emitEvent = useCallback(
    (event, payload) => {
      if (socket?.connected) socket.emit(event, JSON.stringify({ ...payload, token }));
    },
    [socket, token]
  );

  const value = {
    socket,
    token,
    currentUser,
    isConnected,
    emitWithAck,
    emitEvent,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
